import cidrRegex from "cidr-regex";
import ipRegex from "ip-regex";
import { cloneDeep, get, isNil, upperCase } from "lodash-es";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import {
  FormControl,
  InputLabel,
  NativeSelect,
  OutlinedInput,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import TextField from "../../../components/styled/TextField";
import useCurrentClusterSupportsSsh from "../../../hooks/use-current-cluster-supports-ssh";
import criteriaJson from "../../../json/ppl_criteria.json";
import { PplRule } from "../../../store/pomeriumApi";
import AddRemoveActions from "./AddRemoveActions";
import PPLBlock from "./PPLBlock";
import CriteriaSelect from "./conditions/CriteriaSelect";
import OperatorSelect from "./conditions/OperatorSelect";
import RuleFields from "./conditions/RuleFields";
import MultiValueTextFieldGroup from "@/components/MultiValueTextFieldGroup";
import { Criterion } from "@/utils/ppl";

interface ConditionBlockProps {
  readonly: boolean;
  pplArr: any[];
  savePPL(ppl: PplRule[]): void;
  blockIDX: number;
  allowDeny: "allow" | "deny";
  condition: "and" | "or" | "not" | "nor";
  rules: any[];
}

export default function ConditionBlock({
  readonly,
  pplArr,
  savePPL,
  blockIDX,
  allowDeny,
  condition,
  rules,
}: ConditionBlockProps) {
  const [parseError, setParseError] = useState({});

  const handleAdd = () => {
    const arr = cloneDeep(pplArr);
    arr[blockIDX][allowDeny][condition].unshift(
      Criterion.defaultForName("domain").toValue(),
    );
    savePPL(arr);
  };
  const handleRemove = () => {
    const arr = cloneDeep(pplArr);
    delete arr[blockIDX][allowDeny][condition];
    savePPL(arr);
  };

  function shouldUseNewCriteriaComponent(criteriaName: string) {
    return (
      criteriaName.startsWith("claim/") ||
      criteriaName.startsWith("ssh") ||
      [
        "domain",
        "email",
        "http_method",
        "http_path",
        "mcp_tool",
        "source_ip",
      ].includes(criteriaName)
    );
  }

  return (
    <PPLBlock
      depth={1}
      title={upperCase(condition)}
      actions={
        !(rules.length > 0) && (
          <AddRemoveActions
            readonly={readonly}
            onAdd={handleAdd}
            onRemove={handleRemove}
          />
        )
      }
    >
      {rules &&
        !!rules?.length &&
        rules.map((rule, ruleIDX) => {
          const criteria = Object.keys(rule)[0];

          // Updated <CriteriaComponent>, to replace existing <CriteriaSelect>, <OperatorSelect>, <RuleFields>

          if (shouldUseNewCriteriaComponent(criteria)) {
            const criterion = Criterion.fromValue(rule);
            if (!criterion) {
              return <></>;
            }
            return (
              <CriteriaComponent
                key={blockIDX + allowDeny + condition + ruleIDX}
                criterion={criterion}
                onAdd={() => {
                  const arr = cloneDeep(pplArr);
                  arr[blockIDX][allowDeny][condition].splice(
                    ruleIDX,
                    0,
                    cloneDeep(rule),
                  );
                  savePPL(arr);
                }}
                onChange={(criterion: Criterion) => {
                  const arr = cloneDeep(pplArr);
                  arr[blockIDX][allowDeny][condition].splice(
                    ruleIDX,
                    1,
                    criterion.toValue(),
                  );
                  savePPL(arr);
                }}
                onDelete={() => {
                  const arr = cloneDeep(pplArr);
                  arr[blockIDX][allowDeny][condition].splice(ruleIDX, 1);
                  savePPL(arr);
                }}
              />
            );
          }
          // TODO: replace below with only <CriteriaComponent>

          const criteriaVal = rule[criteria];
          let operator: string | null;
          if (typeof criteriaVal === "string") {
            operator = "is";
          } else if (criteriaVal !== null) {
            operator = Object.keys(criteriaVal)[0];
            if (operator == "timezone") {
              operator = Object.keys(criteriaVal)[1];
            }
          } else {
            operator = "";
          }

          const actions = (
            <AddRemoveActions
              readonly={readonly}
              onAdd={() => {
                const arr = cloneDeep(pplArr);
                arr[blockIDX][allowDeny][condition].splice(
                  ruleIDX,
                  0,
                  cloneDeep(rule),
                );
                savePPL(arr);
              }}
              onRemove={() => {
                const arr = cloneDeep(pplArr);
                arr[blockIDX][allowDeny][condition].splice(ruleIDX, 1);
                savePPL(arr);
              }}
            />
          );

          if (
            criteriaVal !== null &&
            (Object.keys(criteriaJson).includes(criteria) ||
              criteria.substring(0, 6) == "claim/")
          ) {
            return (
              <Grid
                container
                key={blockIDX + allowDeny + condition + ruleIDX}
                direction="row"
                justifyContent="space-between"
                alignContent="flex-start"
              >
                <Grid size={2}>
                  <CriteriaSelect
                    readonly={readonly}
                    pplArr={pplArr}
                    savePPL={savePPL}
                    condition={condition}
                    blockIDX={blockIDX}
                    allowDeny={allowDeny}
                    ruleIDX={ruleIDX}
                    criteria={criteria}
                  />
                </Grid>
                <Grid size={2}>
                  <OperatorSelect
                    readonly={readonly}
                    pplArr={pplArr}
                    savePPL={savePPL}
                    condition={condition}
                    blockIDX={blockIDX}
                    allowDeny={allowDeny}
                    ruleIDX={ruleIDX}
                    criteria={criteria}
                    operator={operator}
                  />
                </Grid>
                <Grid sx={{ mt: 1 }} size={6}>
                  <RuleFields
                    readonly={readonly}
                    pplArr={pplArr}
                    savePPL={savePPL}
                    condition={condition}
                    blockIDX={blockIDX}
                    allowDeny={allowDeny}
                    ruleIDX={ruleIDX}
                    criteria={criteria}
                    operator={operator}
                    criteriaVal={criteriaVal}
                  />
                </Grid>
                {actions}
              </Grid>
            );
          }

          const parseKey =
            blockIDX + allowDeny + condition + ruleIDX + "unknownType";
          //unknown criteria type or toggle info
          return (
            <Grid
              container
              key={blockIDX + allowDeny + condition + ruleIDX}
              direction="row"
              justifyContent="space-between"
              alignContent="flex-start"
            >
              <Grid size={6}>
                <Typography variant={"h6"} style={{ marginTop: "15px" }}>
                  Cannot Render Builder For Unknown Criteria Type
                </Typography>
              </Grid>
              <Grid sx={{ mt: 1 }} size={4}>
                <TextField
                  label={"Unknown Type"}
                  fullWidth
                  key={parseKey}
                  //error={parseError[parseKey]}
                  value={JSON.stringify(rule)}
                  onChange={(evt): void => {
                    const arr = cloneDeep(pplArr);
                    try {
                      arr[blockIDX][allowDeny][condition][ruleIDX] = JSON.parse(
                        evt.target.value,
                      );
                      savePPL(arr);
                      setParseError({ ...parseError, [parseKey]: false });
                    } catch {
                      setParseError({ ...parseError, [parseKey]: true });
                    }
                  }}
                />
              </Grid>
              {actions}
            </Grid>
          );
        })}
    </PPLBlock>
  );
}

const sshCriterionOptions: {
  [key: string]: { title: string; defaultData: () => any };
} = {
  ssh_ca: {
    title: "Certificate Authority",
    defaultData: () => [""],
  },
  ssh_publickey: {
    title: "Public Key",
    defaultData: () => [""],
  },
  ssh_username: {
    title: "Username",
    defaultData: () => ({ is: "" }),
  },
  ssh_username_matches_claim: {
    title: "Username matches Claim",
    defaultData: () => "",
  },
  ssh_username_matches_email: {
    title: "Username matches Email",
    defaultData: () => 1,
  },
};

type CriteriaComponentProps = {
  criterion: Criterion;
  onAdd: () => void;
  onChange: (criterion: Criterion) => void;
  onDelete: () => void;
};

function CriteriaComponent({
  criterion,
  onAdd,
  onChange,
  onDelete,
}: CriteriaComponentProps) {
  const criteriaName = criterion.name.startsWith("ssh_")
    ? "ssh"
    : criterion.name;
  const supportsSsh = useCurrentClusterSupportsSsh();

  return (
    <Stack direction="row" spacing={1}>
      <FormControl variant="outlined" margin="dense">
        <InputLabel htmlFor="outlined-criteria-native-simple">
          Criteria
        </InputLabel>
        <NativeSelect
          value={criteriaName}
          onChange={(evt) => {
            onChange(Criterion.defaultForName(evt.target.value));
          }}
          size="small"
          input={<OutlinedInput label="Criteria" />}
          inputProps={{
            name: "Criteria",
          }}
        >
          {Object.keys(criteriaJson).map((critKey) => {
            if (critKey.includes("ssh") && !supportsSsh) {
              return null;
            }
            return (
              <option key={critKey} value={critKey}>
                {get(criteriaJson, critKey + ".name", "")}
              </option>
            );
          })}
        </NativeSelect>
      </FormControl>
      {criteriaName.startsWith("ssh") && (
        <FormControl variant="outlined" margin="dense">
          <InputLabel htmlFor="outlined-criteria-native-simple">
            SSH Criterion
          </InputLabel>
          <NativeSelect
            value={criterion.name}
            onChange={(evt) => {
              const newCriterion = new Criterion(
                evt.target.value,
                null,
                sshCriterionOptions[evt.target.value].defaultData(),
              );
              onChange(newCriterion);
            }}
            size="small"
            input={<OutlinedInput label="SSH Criterion" />}
          >
            {Object.entries(sshCriterionOptions).map(([name, { title }]) => {
              return (
                <option key={name + "sshOption"} value={name}>
                  {title}
                </option>
              );
            })}
          </NativeSelect>
        </FormControl>
      )}
      <CriterionValue criterion={criterion} onChange={onChange} />
      <AddRemoveActions readonly={false} onAdd={onAdd} onRemove={onDelete} />
    </Stack>
  );
}

const PathStringValue = ({
  criterion,
  onChange,
  label,
}: CriterionValueProps) => {
  const handleChangePath = (path: string) => {
    onChange(new Criterion(criterion.name, path, criterion.data));
  };

  const handleChangeValue = (value: string) => {
    onChange(new Criterion(criterion.name, criterion.path, value));
  };

  return (
    <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
      <FormControl
        variant="outlined"
        margin="dense"
        sx={{ flexBasis: 0, flexGrow: 1 }}
      >
        <TextField
          value={`${criterion.path}`}
          onChange={(evt) => handleChangePath(evt.target.value)}
          fullWidth
          label={label}
        />
      </FormControl>
      <FormControl
        variant="outlined"
        margin="dense"
        sx={{ flexBasis: 0, flexGrow: 1 }}
      >
        <TextField
          value={`${criterion.data}`}
          onChange={(evt) => handleChangeValue(evt.target.value)}
          fullWidth
          label={"Value"}
        />
      </FormControl>
    </Stack>
  );
};

const StringValue = ({ criterion, onChange, label }: CriterionValueProps) => {
  const handleChange = (value: string) => {
    onChange(new Criterion(criterion.name, criterion.path, value));
  };

  return (
    <FormControl variant="outlined" margin="dense" sx={{ flexGrow: 1 }}>
      <TextField
        value={`${criterion.data}`}
        onChange={(evt) => handleChange(evt.target.value)}
        fullWidth
        label={label || "Value"}
      />
    </FormControl>
  );
};

const StringListValue = ({
  criterion,
  onChange,
  label,
  validator,
}: CriterionValueProps) => {
  const [criterionData, setCriterionData] = useState(
    Array.isArray(criterion.data) ? criterion.data : [criterion.data],
  );
  const handleChange = (value: any) => {
    onChange(new Criterion(criterion.name, criterion.path, value));
    setCriterionData(value);
  };

  return (
    <FormControl variant="outlined" margin="dense" sx={{ flexGrow: 1 }}>
      <MultiValueTextFieldGroup
        label={label || "Value"}
        values={criterionData}
        onSave={(values: string[]) => {
          handleChange(values);
        }}
        validator={validator}
        fullWidth
      />
    </FormControl>
  );
};

// Handles Operator and StringValue changes
const STRING_MATCHER_OPERATORS = [
  "is",
  "not",
  "contains",
  "ends_with",
  "starts_with",
  "in",
  "not_in",
];

const StringMatcherValue = ({
  criterion,
  onChange,
  label,
}: CriterionValueProps) => {
  const operators =
    get(criteriaJson, criterion.name.split("/")[0] + ".operators") ||
    STRING_MATCHER_OPERATORS;
  const [operator, setOperator] = useState(Object.keys(criterion.data)[0]);
  const [criterionData, setCriterionData] = useState(
    criterion.data?.[operator],
  );

  // ensure operator and criterionData is up to date with newest Criterion state
  useEffect(() => {
    const [nextOperator] = Object.keys(criterion.data || {});
    setOperator(nextOperator);
    setCriterionData(criterion.data?.[nextOperator]);
  }, [criterion]);

  const handleOperatorChange = (evt: any) => {
    const selectedOperator = evt.target.value as string;
    const isMultiValueOperator = ["in", "not_in"].includes(selectedOperator);
    const currentValue = (() => {
      if (isMultiValueOperator) {
        if (Array.isArray(criterionData)) {
          return criterionData;
        }
        if (criterionData === undefined || criterionData === null) {
          return [""];
        }
        return [criterionData];
      }

      if (Array.isArray(criterionData)) {
        return criterionData.length > 0 ? criterionData[0] : "";
      }

      return criterionData ?? "";
    })();
    const newData = {
      [selectedOperator]: currentValue,
    };
    onChange(
      new Criterion(
        criterion.name || "",
        isNil(criterion.path) ? null : criterion.path,
        newData,
      ),
    );
    setOperator(evt.target.value);
    setCriterionData(currentValue);
  };

  const handleValueChange = (newValue: string | string[]) => {
    const isMultiValueOperator = ["in", "not_in"].includes(operator);
    const normalizedValue = isMultiValueOperator
      ? Array.isArray(newValue)
        ? newValue
        : [newValue]
      : Array.isArray(newValue)
        ? newValue[0]
        : newValue;
    const newData = {
      [operator]: normalizedValue,
    };
    onChange(new Criterion(criterion.name, criterion.path, newData));
    setCriterionData(normalizedValue);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
      <FormControl variant="outlined" margin="dense">
        <InputLabel htmlFor="outlined-criteria-native-simple">
          Operator
        </InputLabel>
        <NativeSelect
          value={operator}
          onChange={handleOperatorChange}
          size="small"
          inputProps={{ sx: { minWidth: { lg: "140px" } } }}
          input={<OutlinedInput label="Operator" />}
        >
          {operators.map((option: string) => (
            <option key={option + "operator"} value={option}>
              {option[0].toUpperCase() + option.slice(1).replaceAll("_", " ")}
            </option>
          ))}
        </NativeSelect>
      </FormControl>
      {["in", "not_in"].includes(operator) && Array.isArray(criterionData) ? (
        <FormControl variant="outlined" margin="dense" sx={{ flexGrow: 1 }}>
          <MultiValueTextFieldGroup
            label={label || "Value"}
            values={criterionData}
            onSave={(values: string[]) => {
              handleValueChange(values);
            }}
            fullWidth
          />
        </FormControl>
      ) : (
        <FormControl variant="outlined" margin="dense" sx={{ flexGrow: 1 }}>
          <TextField
            value={criterionData}
            onChange={(evt) => handleValueChange(evt.target.value)}
            fullWidth
            label={label || "Value"}
          />
        </FormControl>
      )}
    </Stack>
  );
};

type CriterionValueDefinition = {
  component: (props: CriterionValueProps) => ReactNode;
  label?: string;
  placeholder?: string;
  validator?: (value: any) => string;
};

const criterionValueDefinitions: { [key: string]: CriterionValueDefinition } = {
  claim: {
    component: PathStringValue,
    label: "Claim",
  },
  domain: {
    component: StringMatcherValue,
    label: "Domain",
    placeholder: "e.g. example.com",
  },
  email: {
    component: StringMatcherValue,
    label: "Email",
    placeholder: "e.g. user@example.com",
  },
  http_method: {
    component: StringMatcherValue,
    label: "HTTP Method",
  },
  http_path: {
    component: StringMatcherValue,
    label: "HTTP Path",
  },

  // SSH
  ssh_ca: {
    component: StringListValue,
    label: "Authorized Key",
    placeholder: "e.g. ssh-ed25519 AAAA example",
  },
  ssh_publickey: {
    component: StringListValue,
    label: "Authorized Key",
    placeholder: "e.g. ssh-ed25519 AAAA example",
  },
  ssh_username: {
    component: StringMatcherValue,
    label: "Username",
    placeholder: "e.g. ubuntu",
  },
  ssh_username_matches_claim: {
    component: StringValue,
    label: "Claim",
    placeholder: "e.g. ssh-groups",
  },
  ssh_username_matches_email: {
    component: () => <></>,
  },

  // MCP
  mcp_tool: {
    component: StringMatcherValue,
    label: "Value",
    placeholder: "database_query",
  },

  source_ip: {
    component: StringListValue,
    label: "IP Address/CIDR Range",
    placeholder: "192.168.1.2",
    validator: (value: string) => {
      return ipRegex({ exact: true }).test(value) ||
        cidrRegex({ exact: true }).test(value)
        ? ""
        : "Address should be an IP address or a CIDR range";
    },
  },
  user: {
    component: StringMatcherValue,
    label: "User ID",
  },
};

type CriterionValueProps = {
  criterion: Criterion;
  onChange: (criteria: Criterion) => void;
  label?: string;
  placeholder?: string;
  validator?: (value: any) => string;
};

function CriterionValue({ criterion, onChange }: CriterionValueProps) {
  const definition = criterionValueDefinitions[criterion.name];
  if (!definition) {
    return <></>;
  }

  return (
    <definition.component
      criterion={criterion}
      onChange={onChange}
      label={definition.label}
      placeholder={definition.placeholder}
      validator={definition.validator}
    />
  );
}
